import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  useFetchReportVersionQuery,
  useFetchCommentsQuery,
} from "../../services/user-reportService";
import CopyVersionModal from "./CopyVersionModal";
import HiddenReportGenerator from "../manager/component/HiddenReportGenerator";

const PDF_BASE_URL =
  process.env.REACT_APP_AZURE_BLOB_BASE_URL ||
  "https://activehrsaprod.blob.core.windows.net/activatehrpdf";

const normalizeArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const ReportVersionsModal = ({ open, onClose, userId, userName }) => {
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [successSnackbar, setSuccessSnackbar] = useState({ open: false, message: "" });
  const [generatingPdfFor, setGeneratingPdfFor] = useState(null);

  const {
    data: versionsData,
    isLoading: versionsLoading,
    isError: versionsError,
  } = useFetchReportVersionQuery(userId, {
    skip: !open || !userId,
  });

  const { data: commentsData, isLoading: commentsLoading } =
    useFetchCommentsQuery(userId, {
      skip: !open || !userId,
    });

  // Return empty array on error (e.g. "Reports not found") or when the API
  // returns a non-array error object so no phantom rows appear in the grid.
  const versions = useMemo(() => {
    if (versionsError) return [];
    return normalizeArray(versionsData);
  }, [versionsData, versionsError]);

  const comments = useMemo(() => normalizeArray(commentsData), [commentsData]);

  const getCommentForVersion = useCallback(
    (versionId) => {
      const match = comments.find((c) => c.report_version_id === versionId);
      return match?.comment_text ?? "—";
    },
    [comments]
  );

  const handleViewReport = useCallback(async (pdfPath) => {
    // Open a blank tab immediately so popup blockers don't block it later
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write("Loading PDF...");
    }

    try {
      if (!pdfPath) {
        // No PDF path — regenerate from scratch
        if (newWindow) newWindow.close();
        setGeneratingPdfFor({ userId, action: "view", pdfPath: null });
        return;
      }

      const pdfUrl = `${PDF_BASE_URL}/${pdfPath}`;

      let azureFileExists = false;
      try {
        const headResponse = await fetch(pdfUrl, { method: "HEAD" });
        azureFileExists = headResponse.ok;
      } catch {
        // CORS may block HEAD — fall back to assuming it exists
        azureFileExists = true;
      }

      if (!azureFileExists) {
        // PDF record in DB but blob missing from Azure — regenerate
        if (newWindow) newWindow.close();
        setGeneratingPdfFor({ userId, action: "view", pdfPath });
        return;
      }

      // PDF is valid — navigate the pre-opened tab to the PDF URL
      if (newWindow) {
        newWindow.location.href = pdfUrl;
      } else {
        window.open(pdfUrl, "_blank");
      }
    } catch (error) {
      if (newWindow) newWindow.close();
      console.error("Error checking PDF:", error);
    }
  }, [userId]);

  const handleOpenCopyModal = useCallback((versionId) => {
    setSelectedVersionId(versionId);
    setCopyModalOpen(true);
  }, []);

  const handleCloseCopyModal = useCallback(() => {
    setCopyModalOpen(false);
    setSelectedVersionId(null);
  }, []);

  const handleCopySuccess = useCallback((message) => {
    setSuccessSnackbar({ open: true, message });
  }, []);

  const handleSnackbarClose = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setSuccessSnackbar({ open: false, message: "" });
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "version_number",
        headerName: "Version Number",
        minWidth: 150,
      },
      {
        field: "comment_text",
        headerName: "Comments",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ py: 1 }}>
            {getCommentForVersion(params.row.id)}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 250,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip title="Copy role review to another user">
              <CopyBtn onClick={() => handleOpenCopyModal(params.row.id)}>
                <ContentCopyIcon fontSize="small" sx={{ mr: 0.5 }} />
                Copy
              </CopyBtn>
            </Tooltip>
            <ViewBtn onClick={() => handleViewReport(params.row.pdf_path)}>
              <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
              View Report
            </ViewBtn>
          </Box>
        ),
      },
    ],
    [getCommentForVersion, handleViewReport, handleOpenCopyModal] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const isLoading = versionsLoading || commentsLoading;

  return (
    <>
      {/* Hidden Report Generator + Overlay — renders outside the Dialog to avoid z-index issues */}
      {generatingPdfFor && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={60} sx={{ color: "#1085AB", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              Regenerating PDF Report...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please wait, this will take a few seconds.
            </Typography>
          </Box>
          <HiddenReportGenerator
            userId={generatingPdfFor.userId}
            action={generatingPdfFor.action}
            existingPdfPath={generatingPdfFor.pdfPath}
            onComplete={(success) => {
              setGeneratingPdfFor(null);
              if (success) {
                setSuccessSnackbar({
                  open: true,
                  message: "PDF generated and opened successfully.",
                });
              }
            }}
          />
        </>
      )}

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { borderRadius: "10px" } }}
      >
        <ModalTitleWrap>
          <Typography sx={{ fontWeight: 600, fontSize: "20px", color: "#000" }}>
            Report Versions{userName ? ` — ${userName}` : ""}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close modal">
            <CloseIcon />
          </IconButton>
        </ModalTitleWrap>

        <DialogContent sx={{ p: 3 }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                py: 4,
              }}
            >
              <CircularProgress size={28} />
              <Typography color="text.secondary">
                Loading report versions…
              </Typography>
            </Box>
          ) : versions.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No report versions found for this user.
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={versions}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              pageSizeOptions={[10, 30]}
              disableRowSelectionOnClick
              disableColumnMenu
              autoHeight
              localeText={{
                noRowsLabel: "No report versions found for this user",
              }}
              sx={{
                "& .MuiDataGrid-iconButtonContainer": {
                  visibility: "visible !important",
                  width: "auto !important",
                  svg: { opacity: "1 !important" },
                },
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Copy role review modal — rendered outside parent Dialog to avoid z-index stacking issues */}
      <CopyVersionModal
        open={copyModalOpen}
        onClose={handleCloseCopyModal}
        onSuccess={handleCopySuccess}
        sourceUserId={userId}
        sourceUserName={userName}
        reportVersionId={selectedVersionId}
      />

      {/* Success Snackbar shown after copy modal closes */}
      <Snackbar
        open={successSnackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ borderRadius: "7px" }}
        >
          {successSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReportVersionsModal;

const ModalTitleWrap = styled(DialogTitle)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px 24px 16px",
  borderBottom: "1px solid #E5E7EB",
}));

const CopyBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "13px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "5px",
  padding: "8px 12px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#d9701c",
    borderColor: "#d9701c",
  },
}));

const ViewBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "13px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#1085AB",
  borderColor: "#1085AB",
  borderRadius: "5px",
  padding: "8px 12px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#0c6d8e",
    borderColor: "#0c6d8e",
  },
}));
